import React, { useEffect, useState } from 'react';

import { Alert, Box, Button, Chip, CircularProgress, Paper, Stack, TextField, Typography } from '@mui/material';

import { FrequentFlyerItemType } from '~/components/LeBusinessTraveller/pages/BusinessDetailsPage/Tabs/FrequentFlyerTab';

import {
  EmployeeLoyaltyPrograms,
  EmployeeLoyaltyProgramsRequest,
  getEmployee,
  getEmployeeLoyaltyPrograms,
  getFrequentFlyerList,
  updateEmployeeLoyaltyPrograms,
} from '~/services/BusinessTraveller/BusinessTravellerService';

import {
  INITIAL_LOYALTY_PROGRAM_MEMBER_NUMBER_MAP,
  LOYALTY_PROGRAMS_CONFIG,
  ProgramNames,
} from './loyaltyProgramsConfig';

// Airline name mapping from business frequent flyer system
const AirlineNameMap: { [key: string]: string } = {
  qantas: 'Qantas Business Rewards',
  virgin: 'Virgin Australia Business Flyer',
};

interface Props {
  user: App.User;
}

type LoyaltyProgramMemberNumber = Record<ProgramNames, string>;

export default function FrequentFlyerMembershipsTab({ user }: Props) {
  const [loyaltyProgramMemberNumber, setLoyaltyProgramMemberNumber] = useState<LoyaltyProgramMemberNumber>(
    INITIAL_LOYALTY_PROGRAM_MEMBER_NUMBER_MAP,
  );
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [corporateAgreements, setCorporateAgreements] = useState<Array<FrequentFlyerItemType>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadExistingData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // First get the basic employee data to get business ID
        const employee = await getEmployee(user.id_member);

        if (!employee?.businessId) {
          setError('This user is not associated with a business');
          setIsLoading(false);
          return;
        }

        setBusinessId(employee.businessId);

        const businessFrequentFlyerData = await getFrequentFlyerList(employee.businessId);
        if (businessFrequentFlyerData?.result && Array.isArray(businessFrequentFlyerData.result)) {
          setCorporateAgreements(businessFrequentFlyerData.result);
        }

        const loyaltyProgramsData = await getEmployeeLoyaltyPrograms(employee.businessId, user.id_member);

        if (loyaltyProgramsData?.result && Array.isArray(loyaltyProgramsData.result)) {
          // Populate loyalty program codes from the response using config
          const loyaltyProgramMemberNumbers: LoyaltyProgramMemberNumber = {
            ...INITIAL_LOYALTY_PROGRAM_MEMBER_NUMBER_MAP,
          };

          LOYALTY_PROGRAMS_CONFIG.forEach((config) => {
            const loyaltyProgram: EmployeeLoyaltyPrograms | undefined = loyaltyProgramsData.result.find(
              (e: EmployeeLoyaltyPrograms) => e.program === config.program && e.type === config.type,
            );

            loyaltyProgramMemberNumbers[config.program] = loyaltyProgram ? loyaltyProgram.memberNumber : '';
          });

          setLoyaltyProgramMemberNumber(loyaltyProgramMemberNumbers);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load existing frequent flyer data');
      } finally {
        setIsLoading(false);
      }
    };

    loadExistingData();
  }, [user.id_member]);

  const handleMemberNumberChange = (id: string, value: string) => {
    setLoyaltyProgramMemberNumber((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setSuccess(false);

    try {
      if (!businessId) {
        setError('Business ID not available for this user');
        return;
      }

      const loyaltyProgramsData: EmployeeLoyaltyProgramsRequest['employeeLoyaltyPrograms'] = [];

      // Build payload from config and state
      LOYALTY_PROGRAMS_CONFIG.forEach((config) => {
        const programMemberNumber: string = loyaltyProgramMemberNumber[config.program] || '';
        loyaltyProgramsData.push({
          program: config.program,
          memberNumber: programMemberNumber.trim(),
          type: config.type,
        });
      });

      const payload: EmployeeLoyaltyProgramsRequest = {
        employeeLoyaltyPrograms: loyaltyProgramsData,
      };

      await updateEmployeeLoyaltyPrograms(businessId, payload);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to update loyalty programs');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading loyalty programs data...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Loyalty programs updated successfully.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {corporateAgreements.length > 0 && (
        <Paper sx={{ p: 3, mb: 3, backgroundColor: 'grey.50' }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
            Corporate Memberships & Fare Modifier Codes
          </Typography>
          <Stack spacing={2}>
            {corporateAgreements.map((agreement, index) => (
              <Box key={agreement.id || index}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {AirlineNameMap[agreement.airlineName] || agreement.airlineName}
                  </Typography>
                  {agreement.verified && <Chip label="Verified" color="success" size="small" />}
                  {agreement.isCorporateAgreement && <Chip label="Corporate" color="primary" size="small" />}
                </Box>
                <Typography variant="body2">
                  Fare Modifier Code:{' '}
                  <Typography component="span" sx={{ fontWeight: 'bold' }}>
                    {agreement.code}
                  </Typography>
                </Typography>
              </Box>
            ))}
          </Stack>
        </Paper>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={3}>
          {LOYALTY_PROGRAMS_CONFIG.map((config) => (
            <TextField
              key={config.program}
              label={config.label}
              value={loyaltyProgramMemberNumber[config.program] || ''}
              onChange={(e) => handleMemberNumberChange(config.program, e.target.value)}
              placeholder={config.placeholder}
              fullWidth
              disabled={isSubmitting}
            />
          ))}

          <Box>
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              {isSubmitting ? 'Updating...' : 'Update Memberships'}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}
